import type { InfiniteData } from "@tanstack/react-query";
import { z } from "zod";
import { queryClient } from "@/lib/api/query-client";
import type { AiLabels, Comment, Conversation, InboxItem, Page } from "@/lib/types";

/** Linkable inbox filters: /inbox?sentiment=negative&assignee=unassigned&id=… */
export const inboxSearchSchema = z.object({
  state: z.enum(["open", "done", "snoozed", "all"]).optional().catch(undefined),
  type: z.enum(["comment", "conversation"]).optional().catch(undefined),
  accountId: z.string().optional().catch(undefined),
  channel: z.enum(["instagram", "facebook_page", "messenger", "whatsapp", "threads", "tiktok"]).optional().catch(undefined),
  intent: z.enum(["question", "price", "purchase", "collab", "complaint", "praise", "spam", "hate", "other"]).optional().catch(undefined),
  sentiment: z.enum(["positive", "neutral", "negative"]).optional().catch(undefined),
  assignee: z.enum(["me", "unassigned", "anyone"]).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  semantic: z.boolean().optional().catch(undefined),
  id: z.string().optional().catch(undefined),
  // deep links from jobs / notifications (`/inbox?comment=<id>`, `/inbox?conversation=<id>`): opened like `id`
  comment: z.string().optional().catch(undefined),
  conversation: z.string().optional().catch(undefined),
  // ALP-65/72: lifecycle + moderation filters
  flag: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/).optional().catch(undefined),
  deleted: z.enum(["show", "only"]).optional().catch(undefined),
  archived: z.enum(["include", "only"]).optional().catch(undefined),
  // people blocked in Malfak: their comments and muted DMs (default: hidden from the inbox)
  muted: z.enum(["include", "only"]).optional().catch(undefined),
  // "All their comments" from a person: this person's comments and conversations
  contactId: z.string().optional().catch(undefined),
});
export type InboxSearch = z.infer<typeof inboxSearchSchema>;

/** API query for a set of filters (state defaults to "open"; `semantic` only when on, since the API coerces "false" to true). */
export function toInboxQuery(s: InboxSearch) {
  return {
    state: s.state === "all" ? undefined : (s.state ?? "open"),
    type: s.type,
    accountId: s.accountId,
    channel: s.channel,
    intent: s.intent,
    sentiment: s.sentiment,
    assignee: s.assignee === "anyone" ? undefined : s.assignee,
    q: s.q?.trim() || undefined,
    semantic: s.semantic && s.q?.trim() ? true : undefined,
    flag: s.flag,
    showDeleted: s.deleted === "show" ? true : undefined,
    onlyDeleted: s.deleted === "only" ? true : undefined,
    archived: s.archived,
    muted: s.muted,
    contactId: s.contactId,
  };
}

export function activeFilterCount(s: InboxSearch) {
  return [s.type, s.accountId, s.channel, s.intent, s.sentiment, s.assignee && s.assignee !== "anyone" ? s.assignee : undefined, s.flag, s.deleted, s.archived, s.muted, s.contactId].filter(Boolean).length;
}

// ------------------------------------------------------------------ item accessors
export const itemId = (it: InboxItem) => (it.type === "comment" ? it.comment.id : it.conversation.id);
export const itemState = (it: InboxItem) => (it.type === "comment" ? it.comment.inboxState : it.conversation.inboxState);
export const itemLabels = (it: InboxItem): AiLabels => (it.type === "comment" ? it.comment.labels : it.conversation.labels);
export const itemAssignee = (it: InboxItem) => (it.type === "comment" ? it.comment.assigneeId : it.conversation.assigneeId);
export const itemAccountId = (it: InboxItem) => (it.type === "comment" ? it.comment.accountId : it.conversation.accountId);
export const itemChannel = (it: InboxItem) => (it.type === "comment" ? it.comment.channel : it.conversation.channel);
export const itemTime = (it: InboxItem) => (it.type === "comment" ? it.comment.createdAt : it.conversation.updatedAt);
export const itemUnread = (it: InboxItem) => (it.type === "conversation" ? it.conversation.unread : 0);
export function itemAuthor(it: InboxItem): { name: string; handle: string; contactId: string | null } {
  if (it.type === "comment") return { name: it.comment.author.username, handle: it.comment.author.username, contactId: it.comment.author.contactId };
  const c = it.conversation.contact;
  return { name: c.displayName, handle: c.identities[0]?.handle ?? c.displayName, contactId: c.id };
}

/** Inbox query cache shape (infinite pages). */
export type InboxPages = InfiniteData<Page<InboxItem>, string | undefined>;

/** Every cached inbox item, deduplicated (used to resolve the selected id). */
export function findCachedItem(id: string): InboxItem | undefined {
  for (const [, data] of queryClient.getQueriesData<InboxPages | Page<InboxItem>>({ queryKey: ["inbox"] })) {
    if (!data) continue;
    const pages = "pages" in data ? data.pages : [data];
    for (const p of pages) for (const it of p.items) if (itemId(it) === id) return it;
  }
  return undefined;
}

type Patch = { comment?: Partial<Comment>; conversation?: Partial<Conversation> };

/** Optimistically patch one or more items in every inbox cache; returns a rollback. */
export function patchItems(ids: string[], patch: Patch): () => void {
  const set = new Set(ids);
  const snapshot = queryClient.getQueriesData({ queryKey: ["inbox"] });
  const apply = (items: InboxItem[]) =>
    items.map((it) => {
      if (!set.has(itemId(it))) return it;
      if (it.type === "comment" && patch.comment) return { ...it, comment: { ...it.comment, ...patch.comment } };
      if (it.type === "conversation" && patch.conversation) return { ...it, conversation: { ...it.conversation, ...patch.conversation } };
      return it;
    });
  queryClient.setQueriesData<InboxPages | Page<InboxItem>>({ queryKey: ["inbox"] }, (old) => {
    if (!old) return old;
    if ("pages" in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: apply(p.items) })) };
    return { ...old, items: apply(old.items) };
  });
  return () => snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

/** Private reply rule: one per comment, within N days (capabilities). */
export function privateReplyState(comment: Comment, windowDays: number | null | undefined, now = Date.now()): "ok" | "used" | "expired" | "unsupported" {
  if (!windowDays) return "unsupported";
  if (comment.privateReplySentAt) return "used";
  if (now - Date.parse(comment.createdAt) > windowDays * 86_400_000) return "expired";
  return "ok";
}

/** 24h messaging window state. */
export function windowState(expiresAt: string | null, now = Date.now()): { state: "open" | "closing" | "closed"; msLeft: number } {
  if (!expiresAt) return { state: "closed", msLeft: 0 };
  const msLeft = Date.parse(expiresAt) - now;
  if (msLeft <= 0) return { state: "closed", msLeft: 0 };
  return { state: msLeft < 3 * 3600_000 ? "closing" : "open", msLeft };
}

export const REPLY_MAX = 2200;
export const DM_MAX = 1000;
